import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Github,
  Target,
  Lightbulb,
  Users,
  Code2,
  Heart,
  Rocket,
  ArrowRight,
} from "lucide-react";

const About = () => {
  const values = [
    {
      icon: <Code2 className="h-6 w-6" />,
      title: "Developer First",
      description:
        "Built by developers who understand the pain points of modern workflows",
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "buy once, keep forever",
      description:
        "Transparent, community-driven development under MIT License",
    },
    {
      icon: <Lightbulb className="h-6 w-6" />,
      title: "Innovation",
      description:
        "Pushing boundaries with AI, automation, and tool integration",
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Community",
      description:
        "Welcoming contributions and feedback from developers worldwide",
    },
  ];

  return (
    <div className="min-h-screen pt-20 pb-20">
      <section className="py-20 bg-gradient-to-b from-purple-950/10 to-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center space-y-6 mb-16">
            <h1 className="text-5xl md:text-6xl font-bold">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                About Cogsmith
              </span>
            </h1>
            <p className="text-xl text-foreground/70">
              Empowering developers with AI-powered automation and productivity
              tools
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-yellow-500/20 rounded-2xl blur-3xl"></div>
              <img
                src="https://images.unsplash.com/photo-1532211779254-6ef4e2b8454c?auto=format&fit=crop&w=800"
                alt="About Cogsmith"
                className="relative rounded-2xl shadow-2xl w-full h-auto"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold">
                Our <span className="text-purple-400">Mission</span>
              </h2>
              <p className="text-lg text-foreground/70">
                Cogsmith was created to bridge the gap between AI capabilities and
                practical developer workflows. We believe that powerful
                automation tools should be accessible, transparent, and easy to
                use.
              </p>
              <p className="text-lg text-foreground/70">
                By combining AI assistants with browser automation and a
                comprehensive tool ecosystem, we're building the productivity
                platform developers have always needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                Our Values
              </span>
            </h2>
            <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
              The principles that guide our development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {values.map((value, index) => (
              <Card
                key={index}
                className="group hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 hover:-translate-y-1 bg-card/50 backdrop-blur-sm border-border/40 hover:border-purple-500/30"
              >
                <CardContent className="p-6 space-y-4 text-center">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-500/20 to-yellow-500/20 flex items-center justify-center text-purple-400 group-hover:scale-110 transition-transform duration-300 mx-auto">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground">
                    {value.title}
                  </h3>
                  <p className="text-foreground/60 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-background to-purple-950/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20">
                  <Target className="h-4 w-4 text-purple-400" />
                  <span className="text-sm text-purple-400 font-medium">
                    Our Vision
                  </span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold">
                  Building the Future of
                  <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                    {" "}
                    Developer Productivity
                  </span>
                </h2>
                <p className="text-lg text-foreground/70">
                  We envision a world where developers can focus on creative
                  problem-solving while AI handles the repetitive tasks. Cogsmith
                  is just the beginning of that journey.
                </p>
                <div className="space-y-3">
                  {[
                    "Seamless AI integration across workflows",
                    "Universal tool compatibility via MCP",
                    "Privacy-focused local processing",
                    "Community-driven feature development",
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 text-foreground/80"
                    >
                      <Rocket className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-purple-500/20 rounded-2xl blur-3xl"></div>
                <img
                  src="https://images.pexels.com/photos/34212896/pexels-photo-34212896.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Developer Workspace"
                  className="relative rounded-2xl shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-4xl mx-auto overflow-hidden bg-gradient-to-br from-purple-950/30 to-teal-950/30 border-purple-500/20">
            <CardContent className="p-12 text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-yellow-500/20 mx-auto">
                <Github className="h-8 w-8 text-purple-400" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold">
                <span className="bg-gradient-to-r from-purple-400 to-yellow-400 bg-clip-text text-transparent">
                  Join Our Community
                </span>
              </h2>
              <p className="text-xl text-foreground/70 max-w-2xl mx-auto">
                Cogsmith is buy once, keep forever and welcomes contributions from developers
                like you. Together, we can build something amazing.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                <a
                  href="https://github.com/newtro/Cogsmith"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-purple-500 to-yellow-500 hover:from-purple-600 hover:to-yellow-600 text-white shadow-lg shadow-purple-500/30 hover:shadow-xl hover:shadow-purple-500/40 transition-all duration-300"
                  >
                    <Github className="mr-2 h-5 w-5" />
                    Contribute on GitHub
                  </Button>
                </a>
                <Link to="/contact">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/50"
                  >
                    Get in Touch
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;